// Working-day arithmetic, mirroring public.leave_working_days
// (supabase/migrations/20260616000000_leave_integrity.sql:24-46).
//
// The server recomputes leave_requests.day_count on every write and its value is
// the one that moves the balance, so this module exists purely so the UI shows
// the SAME number that will be deducted. Before it, the leave form counted
// Mon–Fri and ignored holidays, so an org that works Saturdays — or any range
// containing a holiday — showed the user one figure and deducted another.
//
// Keep it a mirror. In particular the server excludes holidays of EVERY kind,
// including 'optional'; whether an optional holiday should really consume leave
// is a product question, not something to "improve" on one side only.

// Indexed by Date#getDay(). Matches the array the SQL uses to key `workdays`.
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export type Workdays = Record<string, boolean>;

// The same fallback the SQL applies when the org_settings row is missing.
const DEFAULT_WORKDAYS: Workdays = {
  Mon: true,
  Tue: true,
  Wed: true,
  Thu: true,
  Fri: true,
  Sat: false,
  Sun: false,
};

// Parse an ISO date as LOCAL midnight — 'YYYY-MM-DD' alone is treated as UTC by
// Date, which shifts the weekday for anyone behind GMT.
const dowOf = (iso: string): number => new Date(`${iso}T00:00:00`).getDay();

export const isWorkday = (
  iso: string,
  workdays?: Workdays | null,
  holidays?: ReadonlySet<string>,
): boolean => {
  if (holidays?.has(iso)) return false;
  const wd = workdays ?? DEFAULT_WORKDAYS;
  // SQL: coalesce((workdays ->> dow)::boolean, true) — an absent key means working.
  return wd[DOW[dowOf(iso)]] ?? true;
};

// Inclusive list of the chargeable days in a range, oldest first.
export const workingDayList = (
  start: string,
  end?: string,
  workdays?: Workdays | null,
  holidays?: ReadonlySet<string>,
): string[] => {
  if (!start) return [];
  const last = end || start;
  if (last < start) return [];
  const out: string[] = [];
  const cur = new Date(`${start}T00:00:00`);
  const stop = new Date(`${last}T00:00:00`);
  // Guard a pathological range so a typo'd year can't spin: the server caps a
  // request at 366 days anyway.
  for (let i = 0; cur <= stop && i < 400; i++) {
    const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
    if (isWorkday(iso, workdays, holidays)) out.push(iso);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
};

export const workingDays = (
  start: string,
  end?: string,
  workdays?: Workdays | null,
  holidays?: ReadonlySet<string>,
): number => workingDayList(start, end, workdays, holidays).length;

// Today as an ISO date, for the `min`/`max` on a date input. The server compares
// against org_today() (the org's timezone), so this is the UX mirror, not the
// boundary — it can be a day out for a user whose device clock is in another
// timezone, and the DB still refuses.
//
// Re-exported rather than reimplemented: this was a byte-for-byte copy of
// dates.ts's version, which is the module that exists to get local-calendar keys
// right. Kept exported here so existing importers don't have to move.
export { todayIso } from './dates';
